import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon, PrinterIcon, AcademicCapIcon, CalendarDaysIcon,
  BanknotesIcon, ClipboardDocumentListIcon, ArrowsRightLeftIcon,
  ArrowUpCircleIcon, ClockIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtMonth = (m) => { if (!m) return '—'; const [y,mo] = m.split('-'); return new Date(Number(y), Number(mo)-1).toLocaleDateString('en-US',{month:'short',year:'numeric'}); };
const money    = (n) => `Rs. ${Number(n||0).toLocaleString('en-PK')}`;
const moneyHostel = (n) => `Rs. ${Number(n||0).toLocaleString('en-PK',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const pctColor = (p) => p == null ? '#9094a8' : p >= 80 ? '#10c98e' : p >= 60 ? '#f59e0b' : '#ef4444';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sh * { box-sizing:border-box; }
  .sh { font-family:'Sora',sans-serif; color:#1a1d2e; animation:shFade .4s ease both; }
  @keyframes shFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
  .sh-hdr { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .sh-hdr-l { display:flex; align-items:center; gap:12px; }
  .back-btn { width:38px; height:38px; border-radius:10px; border:1px solid #e0e4f0; background:white; display:flex; align-items:center; justify-content:center; cursor:pointer; flex-shrink:0; }
  .back-btn svg { width:18px; height:18px; stroke:#6b7194; stroke-width:2; }
  .sh-title { font-size:1.3rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .sh-sub   { font-size:.76rem; color:#9094a8; margin-top:2px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; } .btn-blue:hover { background:#2d5ef0; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-green { background:#10c98e; color:white; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }

  .profile-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; margin-bottom:1.2rem; display:flex; gap:16px; flex-wrap:wrap; align-items:center; }
  .profile-avatar { width:64px; height:64px; border-radius:16px; background:linear-gradient(135deg,#3b6ef8,#8b5cf6); display:flex; align-items:center; justify-content:center; font-size:1.4rem; font-weight:700; color:white; flex-shrink:0; }
  .profile-name { font-size:1.15rem; font-weight:700; color:#0f1123; }
  .profile-meta { font-size:.78rem; color:#9094a8; margin-top:3px; }
  .profile-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:10px 20px; margin-top:14px; width:100%; }
  .profile-field-label { font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .profile-field-val   { font-size:.82rem; color:#1a1d2e; margin-top:2px; }
  .status-chip { display:inline-flex; font-size:.68rem; font-weight:700; padding:3px 10px; border-radius:99px; }
  .status-chip.active { background:#ecfdf5; color:#10c98e; }
  .status-chip.left   { background:#fef2f2; color:#ef4444; }

  .section { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1.2rem; }
  .section-hdr { display:flex; align-items:center; gap:10px; padding:14px 18px; border-bottom:1px solid #f0f2f8; }
  .section-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .section-icon svg { width:16px; height:16px; stroke-width:1.8; }
  .section-title { font-size:.88rem; font-weight:700; color:#0f1123; }
  .section-body { padding:16px 18px; }
  .empty-note { font-size:.8rem; color:#9094a8; text-align:center; padding:1.5rem; }

  .timeline { display:flex; flex-direction:column; gap:0; }
  .tl-item { display:flex; gap:12px; padding:10px 0; border-bottom:1px solid #f3f5fb; }
  .tl-item:last-child { border-bottom:none; }
  .tl-dot { width:9px; height:9px; border-radius:50%; background:#3b6ef8; margin-top:5px; flex-shrink:0; }
  .tl-dot.held { background:#f59e0b; }
  .tl-dot.left { background:#ef4444; }
  .tl-body { flex:1; }
  .tl-main { font-size:.82rem; font-weight:600; color:#1a1d2e; }
  .tl-meta { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .tl-note { font-size:.74rem; color:#6b7194; margin-top:3px; font-style:italic; }

  .yr-block { border:1px solid #e8ebf4; border-radius:12px; padding:14px 16px; margin-bottom:10px; }
  .yr-block:last-child { margin-bottom:0; }
  .yr-hdr { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; flex-wrap:wrap; gap:8px; }
  .yr-title { font-size:.86rem; font-weight:700; color:#0f1123; }
  .yr-pct { font-size:.9rem; font-weight:700; font-family:'JetBrains Mono',monospace; }
  .subj-row { display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #f5f6fb; font-size:.78rem; }
  .subj-row:last-child { border-bottom:none; }
  .subj-name { font-weight:600; color:#1a1d2e; }
  .subj-detail { color:#9094a8; font-size:.72rem; margin-top:2px; }
  .subj-score { font-family:'JetBrains Mono',monospace; font-weight:600; }

  .att-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(140px,1fr)); gap:10px; }
  .att-card { border:1px solid #e8ebf4; border-radius:10px; padding:12px; text-align:center; }
  .att-year { font-size:.72rem; font-weight:700; color:#9094a8; margin-bottom:6px; }
  .att-pct  { font-size:1.3rem; font-weight:700; font-family:'JetBrains Mono',monospace; }
  .att-detail { font-size:.68rem; color:#9094a8; margin-top:4px; }

  .tbl-wrap { overflow-x:auto; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl th { padding:8px 10px; text-align:center !important; font-size:.63rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; border-bottom:1px solid #edf0f9; white-space:nowrap; }
  .tbl td { padding:9px 10px; font-size:.78rem; border-bottom:1px solid #f5f6fb; vertical-align:middle; text-align:center !important; }
  .tbl tr:last-child td { border-bottom:none; }
  .badge { display:inline-flex; font-size:.66rem; font-weight:600; padding:2px 8px; border-radius:99px; white-space:nowrap; }
  .badge.approved,.badge.paid { background:#ecfdf5; color:#10c98e; }
  .badge.rejected,.badge.overdue { background:#fef2f2; color:#ef4444; }
  .badge.pending,.badge.unpaid { background:#fffbeb; color:#f59e0b; }
  .badge.waived { background:#eef2ff; color:#3b6ef8; }
  .badge.pending_verification { background:#f3f0ff; color:#8b5cf6; }

  .loading { display:flex; align-items:center; justify-content:center; height:300px; gap:10px; color:#9094a8; }
  .spinner { width:20px; height:20px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .req-banner { background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:10px 14px; font-size:.78rem; color:#c2410c; margin-bottom:1.2rem; display:flex; align-items:center; gap:8px; flex-wrap:wrap; justify-content:space-between; }
  .req-status-pill { display:inline-flex; font-size:.7rem; font-weight:700; padding:3px 10px; border-radius:99px; }
  .req-status-pill.pending  { background:#fffbeb; color:#f59e0b; }
  .req-status-pill.approved { background:#ecfdf5; color:#10c98e; }
  .req-status-pill.rejected { background:#fef2f2; color:#ef4444; }

  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; padding:24px; width:100%; max-width:440px; box-shadow:0 20px 60px rgba(0,0,0,.2); }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; margin-bottom:14px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; display:block; margin-bottom:6px; }
  .form-textarea { width:100%; font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; outline:none; resize:vertical; min-height:80px; }
  .form-textarea:focus { border-color:#3b6ef8; background:white; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:16px; }

  @media (max-width:768px) {
    .sh-title { font-size:1.1rem; }
    .profile-card { flex-direction:column; align-items:flex-start; }
  }
`;

function Section({ icon, iconBg, iconColor, title, empty, children }) {
  return (
    <div className="section">
      <div className="section-hdr">
        <div className="section-icon" style={{ background:iconBg, color:iconColor }}>{icon}</div>
        <div className="section-title">{title}</div>
      </div>
      <div className="section-body">
        {empty ? <div className="empty-note">Nothing on record yet.</div> : children}
      </div>
    </div>
  );
}

export default function StudentHistory({ role }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = role === 'admin';
  const apiPrefix = isAdmin ? '/admin' : '/coordinator';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myRequests, setMyRequests] = useState([]);
  const [showReqModal, setShowReqModal] = useState(false);
  const [reqReason, setReqReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`${apiPrefix}/students/${id}/history`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load student history'))
      .finally(() => setLoading(false));
  }, [apiPrefix, id]);

  const loadRequests = useCallback(() => {
    if (isAdmin) return;
    api.get('/coordinator/requests').then(r => setMyRequests(r.data || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => { load(); loadRequests(); }, [load, loadRequests]);

  const myDownloadRequests = myRequests.filter(r => r.type === 'student_history_download' && String(r.payload?.studentId) === String(id));
  const approvedReq = myDownloadRequests.find(r => r.status === 'approved');
  const pendingReq   = myDownloadRequests.find(r => r.status === 'pending');
  const canDownload  = isAdmin || !!approvedReq;

  const submitDownloadRequest = async () => {
    if (!reqReason.trim()) return toast.error('Reason is required');
    setSubmitting(true);
    try {
      await api.post('/coordinator/requests', {
        type: 'student_history_download',
        payload: { studentId: id, studentName: data?.student?.name, admissionNo: data?.student?.admissionNo, reason: reqReason },
      });
      toast.success('Download request submitted — awaiting admin approval');
      setShowReqModal(false); setReqReason('');
      loadRequests();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit request'); }
    finally { setSubmitting(false); }
  };

  const handlePrint = () => {
    if (!data) return;
    const { student, promotions, transfers, leaves, fees, academicHistory, attendanceHistory, timeline: studentTimeline = [] } = data;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>${student.name} — Full History</title>
      <style>
        body{font-family:Arial,sans-serif;color:#1a1d2e;padding:30px;font-size:13px;}
        h1{font-size:20px;margin-bottom:2px;} h2{font-size:15px;margin:22px 0 8px;border-bottom:2px solid #3b6ef8;padding-bottom:4px;}
        .meta{color:#666;font-size:12px;margin-bottom:14px;}
        table{width:100%;border-collapse:collapse;margin-bottom:10px;}
        th,td{border:1px solid #ddd;padding:6px 9px;text-align:left;font-size:12px;}
        th{background:#f5f6fb;}
        .grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:10px;}
        .field-label{font-size:10px;color:#888;text-transform:uppercase;} .field-val{font-size:13px;}
        @media print { body{padding:10px;} }
      </style></head><body>
        <h1>${student.name}</h1>
        <div class="meta">Admission No: ${student.admissionNo} · Roll No: ${student.rollNo} · Class: ${student.class?.name || '—'} - ${student.section}</div>

        <h2>Profile</h2>
        <div class="grid">
          <div><div class="field-label">Email</div><div class="field-val">${student.email || '—'}</div></div>
          <div><div class="field-label">Date of Birth</div><div class="field-val">${fmtDate(student.dateOfBirth)}</div></div>
          <div><div class="field-label">Joining Date</div><div class="field-val">${fmtDate(student.joiningDate)}</div></div>
          <div><div class="field-label">Parent Name</div><div class="field-val">${student.parentName || '—'}</div></div>
          <div><div class="field-label">Parent Contact</div><div class="field-val">${student.parentContact || '—'}</div></div>
          <div><div class="field-label">Parent/Guardian CNIC</div><div class="field-val">${student.parentCnic || '—'}</div></div>
          <div><div class="field-label">Address</div><div class="field-val">${student.address || '—'}</div></div>
        </div>
        ${!student.isActive ? `<p><strong>Left:</strong> ${fmtDate(student.leftAt)} — ${student.leftReason || ''} ${student.leftNote ? '('+student.leftNote+')' : ''}</p>` : ''}

        <h2>Student Timeline</h2>
        ${studentTimeline.length === 0 ? '<p>No timeline records.</p>' : `
          <table><thead><tr><th>Date</th><th>Event</th><th>Details</th></tr></thead><tbody>
            ${studentTimeline.map(t => t.kind === 'joined'
              ? `<tr><td>${fmtDate(t.date)}</td><td>Joined</td><td>—</td></tr>`
              : `<tr><td>${fmtDate(t.date)}</td><td>Left${t.reason ? ' — '+t.reason : ''}</td><td>${t.note || ''}${t.by ? ' (by '+t.by+')' : ''}</td></tr>`
            ).join('')}
          </tbody></table>`}

        <h2>Class &amp; Section Journey</h2>
        ${promotions.length === 0 && transfers.length === 0 ? '<p>No promotion or transfer records.</p>' : `
          <table><thead><tr><th>Date</th><th>Type</th><th>From</th><th>To</th><th>Note / Reason</th></tr></thead><tbody>
            ${promotions.map(p => `<tr><td>${fmtDate(p.promotedAt)}</td><td>${p.status==='held_back'?'Held Back':'Promotion'}</td><td>${p.fromClass||'—'} - ${p.fromSection} (${p.fromYear})</td><td>${p.toClass||'—'} - ${p.toSection} (${p.toYear})</td><td>${p.note||'—'}</td></tr>`).join('')}
            ${transfers.map(t => `<tr><td>${fmtDate(t.transferDate)}</td><td>Section Transfer</td><td>${t.fromClass||'—'} - ${t.fromSection}</td><td>${t.toClass||'—'} - ${t.toSection}</td><td>${t.reason||'—'}</td></tr>`).join('')}
          </tbody></table>`}

        <h2>Academic History</h2>
        ${academicHistory.length === 0 ? '<p>No academic records.</p>' : academicHistory.map(y => `
          <h3 style="font-size:13px;margin:12px 0 4px;">${y.academicYear} — Overall: ${y.overallPercentage != null ? y.overallPercentage+'%' : '—'}</h3>
          ${y.subjects.map(s => `
            <table><thead><tr><th>${s.subject}</th>${s.entries.map(e=>`<th>${e.typeName}</th>`).join('')}</tr></thead>
            <tbody><tr><td>Marks</td>${s.entries.map(e=>`<td>${e.isAbsent?'Abs':e.isNA?'N/A':(e.obtained!=null?e.obtained:'—')} / ${e.maxMarks}</td>`).join('')}</tr></tbody></table>
          `).join('')}
        `).join('')}

        <h2>Attendance Summary</h2>
        ${attendanceHistory.length === 0 ? '<p>No attendance records.</p>' : `
          <table><thead><tr><th>Year</th><th>Present</th><th>Absent</th><th>Late</th><th>On Leave</th><th>%</th></tr></thead><tbody>
            ${attendanceHistory.map(a => `<tr><td>${a.academicYear}</td><td>${a.present}</td><td>${a.absent}</td><td>${a.late}</td><td>${a.onLeave}</td><td>${a.percentage!=null?a.percentage+'%':'—'}</td></tr>`).join('')}
          </tbody></table>`}

        <h2>Leave History</h2>
        ${leaves.length === 0 ? '<p>No leave records.</p>' : `
          <table><thead><tr><th>Dates</th><th>Reason</th><th>Status</th><th>Review Note</th></tr></thead><tbody>
            ${leaves.map(l => `<tr><td>${fmtDate(l.startDate)} – ${fmtDate(l.endDate)}</td><td>${l.reason||'—'}</td><td>${l.status}</td><td>${l.reviewNote||'—'}</td></tr>`).join('')}
          </tbody></table>`}

        <h2>Fee History</h2>
        ${fees.length === 0 ? '<p>No fee records.</p>' : `
          <table><thead><tr><th>Month</th><th>Amount</th><th>Hostel Fee</th><th>Discount</th><th>Total Due</th><th>Status</th><th>Paid</th></tr></thead><tbody>
            ${fees.map(f => `<tr><td>${fmtMonth(f.month)}</td><td>${money(f.amount)}</td><td>${moneyHostel(f.hostelFee)}</td><td>${money(f.discount)}</td><td>${money(f.totalDue)}</td><td>${f.status}</td><td>${money(f.paidAmount)}</td></tr>`).join('')}
          </tbody></table>`}

        <script>window.onload = () => window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  if (loading) return (
    <div className="sh"><style>{css}</style>
      <div className="loading"><div className="spinner"/>Loading student history…</div>
    </div>
  );

  if (!data) return (
    <div className="sh"><style>{css}</style>
      <div className="empty-note">Could not load this student's history.</div>
    </div>
  );

  const { student, promotions, transfers, leaves, fees, academicHistory, attendanceHistory, timeline: studentTimeline = [] } = data;

  const journey = [
    ...promotions.map(p => ({ kind: 'promotion', date: p.promotedAt, ...p })),
    ...transfers.map(t => ({ kind: 'transfer', date: t.transferDate, ...t })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="sh"><style>{css}</style>
      <div className="sh-hdr">
        <div className="sh-hdr-l">
          <button className="back-btn" onClick={() => navigate(-1)}><ArrowLeftIcon/></button>
          <div>
            <div className="sh-title">Full Student History</div>
            <div className="sh-sub">Complete record from admission to today</div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {!isAdmin && !canDownload && (
            <button className="btn btn-ghost" onClick={() => setShowReqModal(true)} disabled={!!pendingReq}>
              <ClockIcon/>{pendingReq ? 'Request Pending Approval' : 'Request Download/Print'}
            </button>
          )}
          <button className="btn btn-blue" onClick={handlePrint} disabled={!canDownload}>
            <PrinterIcon/>Download / Print
          </button>
        </div>
      </div>

      {!isAdmin && pendingReq && (
        <div className="req-banner">
          <span>📋 Your download request for this student is awaiting admin approval.</span>
          <span className="req-status-pill pending">Pending</span>
        </div>
      )}
      {!isAdmin && approvedReq && (
        <div className="req-banner" style={{ background:'#ecfdf5', borderColor:'#a7f3d0', color:'#065f46' }}>
          <span>✓ Your download request was approved — you can print/download this student's history.</span>
          <span className="req-status-pill approved">Approved</span>
        </div>
      )}

      <div className="profile-card">
        <div className="profile-avatar">{(student.name||'?').split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)}</div>
        <div style={{ flex:1, minWidth:200 }}>
          <div className="profile-name">{student.name} <span className={`status-chip ${student.isActive?'active':'left'}`} style={{marginLeft:8}}>{student.isActive ? 'Active' : 'Left'}</span></div>
          <div className="profile-meta">{student.admissionNo} · Roll {student.rollNo} · {student.class?.name} - {student.section}</div>
          <div className="profile-grid">
            <div><div className="profile-field-label">Email</div><div className="profile-field-val">{student.email || '—'}</div></div>
            <div><div className="profile-field-label">Date of Birth</div><div className="profile-field-val">{fmtDate(student.dateOfBirth)}</div></div>
            <div><div className="profile-field-label">Joining Date</div><div className="profile-field-val">{fmtDate(student.joiningDate)}</div></div>
            <div><div className="profile-field-label">Parent Name</div><div className="profile-field-val">{student.parentName || '—'}</div></div>
            <div><div className="profile-field-label">Parent Contact</div><div className="profile-field-val">{student.parentContact || '—'}</div></div>
            <div><div className="profile-field-label">Parent Email</div><div className="profile-field-val">{student.parentEmail || '—'}</div></div>
            <div><div className="profile-field-label">Parent/Guardian CNIC</div><div className="profile-field-val">{student.parentCnic || '—'}</div></div>
            <div style={{ gridColumn:'1/-1' }}><div className="profile-field-label">Address</div><div className="profile-field-val">{student.address || '—'}</div></div>
          </div>
          {!student.isActive && (
            <div style={{ marginTop:10, fontSize:'.78rem', color:'#ef4444' }}>
              Left on {fmtDate(student.leftAt)} — {student.leftReason || '—'} {student.leftNote ? `(${student.leftNote})` : ''}
            </div>
          )}
        </div>
      </div>

      <Section icon={<ClockIcon/>} iconBg="#f0fdfa" iconColor="#0d9488" title="Student Timeline" empty={studentTimeline.length === 0}>
        <div className="timeline">
          {studentTimeline.map((t, i) => (
            <div key={i} className="tl-item">
              <div className={`tl-dot${t.kind === 'left' ? ' left' : ''}`}/>
              <div className="tl-body">
                <div className="tl-main">{t.kind === 'joined' ? 'Joined' : `Left${t.reason ? ` — ${t.reason}` : ''}`}</div>
                <div className="tl-meta">{fmtDate(t.date)}{t.by ? ` · by ${t.by}` : ''}</div>
                {t.note && <div className="tl-note">{t.note}</div>}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={<ArrowsRightLeftIcon/>} iconBg="#eef2ff" iconColor="#3b6ef8" title="Class & Section Journey" empty={journey.length === 0}>
        <div className="timeline">
          {journey.map((j, i) => (
            <div key={i} className="tl-item">
              <div className={`tl-dot${j.status==='held_back'?' held':''}`}/>
              <div className="tl-body">
                <div className="tl-main">
                  {j.kind === 'promotion'
                    ? (j.status === 'held_back' ? `Held back in ${j.fromClass} - ${j.fromSection}` : `Promoted: ${j.fromClass} - ${j.fromSection} → ${j.toClass} - ${j.toSection}`)
                    : `Section Transfer: ${j.fromClass} - ${j.fromSection} → ${j.toClass} - ${j.toSection}`}
                </div>
                <div className="tl-meta">{fmtDate(j.date)} {j.kind==='promotion' ? `· ${j.fromYear} → ${j.toYear}` : `· ${j.academicYear}`} {(j.promotedBy||j.transferredBy) ? `· by ${j.promotedBy||j.transferredBy}` : ''}</div>
                {(j.note || j.reason) && <div className="tl-note">{j.note || j.reason}</div>}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={<AcademicCapIcon/>} iconBg="#f3f0ff" iconColor="#8b5cf6" title="Academic History" empty={academicHistory.length === 0}>
        {academicHistory.map(y => (
          <div key={y.academicYear} className="yr-block">
            <div className="yr-hdr">
              <div className="yr-title">{y.academicYear}</div>
              <div className="yr-pct" style={{ color: pctColor(y.overallPercentage) }}>{y.overallPercentage != null ? `${y.overallPercentage}%` : '—'}</div>
            </div>
            {y.subjects.map(s => (
              <div key={s.subject} className="subj-row">
                <div>
                  <div className="subj-name">{s.subject}</div>
                  <div className="subj-detail">{s.entries.map(e => e.typeName).join(', ')}</div>
                </div>
                <div className="subj-score">
                  {s.entries.reduce((sum,e)=>sum+(e.obtained||0),0)} / {s.entries.reduce((sum,e)=>sum+e.maxMarks,0)}
                </div>
              </div>
            ))}
          </div>
        ))}
      </Section>

      <Section icon={<CalendarDaysIcon/>} iconBg="#ecfdf5" iconColor="#10c98e" title="Attendance Summary" empty={attendanceHistory.length === 0}>
        <div className="att-grid">
          {attendanceHistory.map(a => (
            <div key={a.academicYear} className="att-card">
              <div className="att-year">{a.academicYear}</div>
              <div className="att-pct" style={{ color: pctColor(a.percentage) }}>{a.percentage != null ? `${a.percentage}%` : '—'}</div>
              <div className="att-detail">{a.present}P · {a.absent}A · {a.late}L · {a.onLeave}OL</div>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={<ClipboardDocumentListIcon/>} iconBg="#fffbeb" iconColor="#f59e0b" title="Leave History" empty={leaves.length === 0}>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Dates</th><th>Reason</th><th>Status</th><th>Review Note</th></tr></thead>
            <tbody>
              {leaves.map(l => (
                <tr key={l._id}>
                  <td>{fmtDate(l.startDate)} – {fmtDate(l.endDate)}</td>
                  <td>{l.reason}</td>
                  <td><span className={`badge ${l.status}`}>{l.status}</span></td>
                  <td>{l.reviewNote || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section icon={<BanknotesIcon/>} iconBg="#eef2ff" iconColor="#3b6ef8" title="Fee History" empty={fees.length === 0}>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Month</th><th>Amount</th><th>Hostel Fee</th><th>Discount</th><th>Total Due</th><th>Paid</th><th>Status</th></tr></thead>
            <tbody>
              {fees.map(f => (
                <tr key={f._id}>
                  <td>{fmtMonth(f.month)}</td>
                  <td>{money(f.amount)}</td>
                  <td>{moneyHostel(f.hostelFee)}</td>
                  <td>{money(f.discount)}</td>
                  <td>{money(f.totalDue)}</td>
                  <td>{money(f.paidAmount)}</td>
                  <td><span className={`badge ${f.status}`}>{f.status.replace('_',' ')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {showReqModal && createPortal(
        <div className="modal-backdrop" onClick={() => setShowReqModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Request Download / Print Access</div>
            <label className="form-label">Reason</label>
            <textarea className="form-textarea" required value={reqReason} onChange={e => setReqReason(e.target.value)} placeholder="Why do you need to download this student's full history?"/>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setShowReqModal(false)}><XMarkIcon/>Cancel</button>
              <button className="btn btn-green" onClick={submitDownloadRequest} disabled={submitting}>
                <ArrowUpCircleIcon/>{submitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
